import { BACKEND_API_URL } from "./apiClient"

export type UsernameAvailabilityReason = "invalid" | "available" | "taken" | "error"

export type UsernameAvailabilityResult = {
  username: string
  available: boolean
  reason: UsernameAvailabilityReason
}

/** Avoid indefinite "Checking…" when the API/proxy hangs. */
const USERNAME_AVAILABILITY_TIMEOUT_MS = 8_000

export class UsernameAvailabilityHttpError extends Error {
  readonly status: number
  readonly retryAfterSeconds: number | null

  constructor(status: number, message: string, retryAfterSeconds: number | null = null) {
    super(message)
    this.name = "UsernameAvailabilityHttpError"
    this.status = status
    this.retryAfterSeconds = retryAfterSeconds
  }
}

function parseRetryAfterSeconds(header: string | null): number | null {
  if (!header) return null
  const asInt = Number.parseInt(header, 10)
  if (Number.isFinite(asInt) && asInt >= 0) return asInt
  return null
}

function mergeAbortSignals(
  external: AbortSignal | undefined,
  timeoutMs: number
): { signal: AbortSignal; cleanup: () => void } {
  const controller = new AbortController()
  const onExternalAbort = () => {
    if (!controller.signal.aborted) {
      controller.abort(external?.reason)
    }
  }

  if (external) {
    if (external.aborted) {
      controller.abort(external.reason)
    } else {
      external.addEventListener("abort", onExternalAbort, { once: true })
    }
  }

  const timeoutId = window.setTimeout(() => {
    if (!controller.signal.aborted) {
      controller.abort(new DOMException("Username availability check timed out", "TimeoutError"))
    }
  }, timeoutMs)

  return {
    signal: controller.signal,
    cleanup: () => {
      window.clearTimeout(timeoutId)
      if (external) {
        external.removeEventListener("abort", onExternalAbort)
      }
    },
  }
}

function asResult(
  username: string,
  body: Partial<UsernameAvailabilityResult>
): UsernameAvailabilityResult {
  const name = typeof body.username === "string" ? body.username : username
  const reason = body.reason

  if (reason === "available" && body.available !== false) {
    return { username: name, available: true, reason: "available" }
  }
  if (reason === "taken") {
    return { username: name, available: false, reason: "taken" }
  }
  if (reason === "invalid") {
    return { username: name, available: false, reason: "invalid" }
  }
  if (reason === "error") {
    return { username: name, available: false, reason: "error" }
  }
  // Boolean-only payloads — never invent available without an explicit true.
  if (body.available === true) {
    return { username: name, available: true, reason: "available" }
  }
  if (body.available === false) {
    return { username: name, available: false, reason: "taken" }
  }
  throw new Error("Unexpected username availability response")
}

export async function checkUsernameAvailability(
  username: string,
  signal?: AbortSignal
): Promise<UsernameAvailabilityResult> {
  const params = new URLSearchParams({ username })
  const { signal: mergedSignal, cleanup } = mergeAbortSignals(
    signal,
    USERNAME_AVAILABILITY_TIMEOUT_MS
  )
  try {
    const response = await fetch(
      `${BACKEND_API_URL}/auth/username-available?${params.toString()}`,
      {
        method: "GET",
        headers: { Accept: "application/json" },
        signal: mergedSignal,
      }
    )
    if (!response.ok) {
      if (response.status === 429) {
        throw new UsernameAvailabilityHttpError(
          429,
          "Too many username checks. Please wait a moment and try again.",
          parseRetryAfterSeconds(response.headers.get("Retry-After"))
        )
      }
      // 503 / 5xx: verify failed closed on the server — surface as error, not available.
      throw new UsernameAvailabilityHttpError(
        response.status,
        "Could not check username availability"
      )
    }
    const body = (await response.json()) as Partial<UsernameAvailabilityResult>
    return asResult(username, body)
  } finally {
    cleanup()
  }
}

